import { useState } from "react";
import {
  BookOpen, Copy, Check, ExternalLink, ArrowRight, Info,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Iter 101 Phase 2 · Session 3 — Runbook that walks the operator through
// wiring TalentLMS to Conduit's OIDC identity provider. Rendered as a
// dialog from the OAuth2 admin panel so the setup instructions are one
// click away when a client is being created.

function Copyable({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Clipboard blocked"); }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      {label && <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>}
      <div className="mt-0.5 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 font-mono text-xs text-slate-800" data-testid={`${testid}-value`}>
          {value}
        </code>
        <Button size="sm" variant="outline" onClick={doCopy} data-testid={`${testid}-copy`} className="h-7">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="grid grid-cols-[32px_1fr] gap-3">
      <div className="mt-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 font-heading text-xs font-bold text-primary">
        {n}
      </div>
      <div className="min-w-0">
        <div className="font-heading text-sm font-semibold text-slate-800">{title}</div>
        <div className="mt-1 space-y-2 text-sm text-slate-600">{children}</div>
      </div>
    </div>
  );
}

export function TalentLMSRunbook({ open, onOpenChange }) {
  const origin = window.location.origin;
  const discovery = `${origin}/api/oauth/.well-known/openid-configuration`;
  const authorize = `${origin}/api/oauth/authorize`;
  const tokenEp = `${origin}/api/oauth/token`;
  const userinfoEp = `${origin}/api/oauth/userinfo`;
  const jwksEp = `${origin}/api/oauth/jwks.json`;
  const talentlmsSsoUrl = "https://united.talentlms.com/index/ssologin/service:oidc?";
  const talentlmsRedirectUri = "https://united.talentlms.com/index/ssologin";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="tlms-runbook-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" /> TalentLMS &rarr; Conduit OIDC setup
          </DialogTitle>
          <DialogDescription>
            One-time configuration to make TalentLMS delegate authentication to Conduit. After this is done, any Conduit user who launches Training lands directly on their TalentLMS dashboard as themselves &mdash; no more cross-user session leaks and no more &ldquo;admin can&rsquo;t auto-login&rdquo; errors.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-slate-600" data-testid="tlms-runbook-prereqs">
          <div className="mb-1 flex items-center gap-1.5 font-semibold text-primary">
            <Info className="h-3.5 w-3.5" /> Before you begin
          </div>
          <ul className="list-disc space-y-0.5 pl-5">
            <li>You need TalentLMS <b>Pro plan or higher</b> (OIDC SSO isn&rsquo;t on Basic/Plus).</li>
            <li>You need <b>super-admin</b> access in both Conduit and TalentLMS.</li>
            <li>Have this Conduit tab and a TalentLMS admin tab open side-by-side.</li>
          </ul>
        </div>

        <div className="mt-5 space-y-5">
          <Step n={1} title="Register TalentLMS as an OAuth2 client in Conduit">
            <p>Click <b>New client</b> in the panel behind this dialog. Fill in:</p>
            <ul className="ml-4 list-disc space-y-1 text-xs">
              <li><b>Name</b>: <code className="rounded bg-slate-100 px-1 font-mono">TalentLMS</code></li>
              <li><b>Redirect URIs</b>:</li>
            </ul>
            <Copyable value={talentlmsRedirectUri} testid="tlms-runbook-redirect" />
            <ul className="ml-4 list-disc space-y-1 text-xs">
              <li><b>Allowed scopes</b>: <code className="mx-0.5 rounded bg-slate-100 px-1 font-mono">openid profile email groups partner_org</code></li>
              <li><b>Trusted first-party client</b>: <span className="rounded bg-emerald-100 px-1 font-semibold text-emerald-800">ON</span> &mdash; skips the consent screen for internal users.</li>
            </ul>
            <p className="text-xs text-slate-500">Click <b>Create client</b>. Copy the <b>Client ID</b> and <b>Client Secret</b> shown in the reveal dialog &mdash; the secret is only visible once.</p>
          </Step>

          <Step n={2} title="Open TalentLMS' OIDC settings">
            <p>In TalentLMS, go to <b>Account &amp; Settings &rarr; Users &rarr; Sign-in / Sign-up options &rarr; Single Sign-On (SSO)</b>. Choose <b>OpenID Connect</b> as the SSO integration type.</p>
            <p className="text-xs text-slate-500">The path is under &ldquo;Domain&rdquo; on some tenants. Look for the <b>OIDC</b> tab if you don&rsquo;t see it immediately.</p>
          </Step>

          <Step n={3} title="Paste Conduit's OIDC endpoints into TalentLMS">
            <p>Most TalentLMS OIDC forms accept a <b>Discovery URL</b> that auto-fills every endpoint:</p>
            <Copyable label="OIDC Discovery URL" value={discovery} testid="tlms-runbook-discovery" />
            <p className="text-xs text-slate-500">If the TalentLMS UI asks for individual endpoints instead, use these:</p>
            <Copyable label="Issuer" value={`${origin}/api/oauth`} testid="tlms-runbook-issuer" />
            <Copyable label="Authorization endpoint" value={authorize} testid="tlms-runbook-authz" />
            <Copyable label="Token endpoint" value={tokenEp} testid="tlms-runbook-token" />
            <Copyable label="UserInfo endpoint" value={userinfoEp} testid="tlms-runbook-userinfo" />
            <Copyable label="JWKS URI" value={jwksEp} testid="tlms-runbook-jwks" />
          </Step>

          <Step n={4} title="Paste the client credentials">
            <p>Paste the <b>Client ID</b> and <b>Client Secret</b> you copied in Step 1 into TalentLMS&rsquo; OIDC form. For &ldquo;Client Authentication Method&rdquo;, choose <b>client_secret_basic</b> or <b>client_secret_post</b> &mdash; either works. For &ldquo;Scope&rdquo;, enter <code className="mx-0.5 rounded bg-slate-100 px-1 font-mono">openid profile email groups partner_org</code>.</p>
          </Step>

          <Step n={5} title="Map Conduit claims to TalentLMS fields">
            <p>Under &ldquo;User attribute mapping&rdquo; in TalentLMS, wire Conduit&rsquo;s userinfo claims to TalentLMS user fields:</p>
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold">TalentLMS field</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Conduit claim</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  <tr><td className="px-2 py-1.5 font-mono">login</td>            <td className="px-2 py-1.5 font-mono">preferred_username</td><td className="px-2 py-1.5">Always the user's email — every TalentLMS username must equal the email</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">email</td>            <td className="px-2 py-1.5 font-mono">email</td>              <td className="px-2 py-1.5">&mdash;</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">first_name</td>       <td className="px-2 py-1.5 font-mono">given_name</td>         <td className="px-2 py-1.5">&mdash;</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">last_name</td>        <td className="px-2 py-1.5 font-mono">family_name</td>        <td className="px-2 py-1.5">&mdash;</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">avatar</td>           <td className="px-2 py-1.5 font-mono">picture</td>            <td className="px-2 py-1.5">Optional</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">branch</td>           <td className="px-2 py-1.5 font-mono">partner_type</td>       <td className="px-2 py-1.5">Reseller / Agent / Customer &rarr; matching branch</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">groups</td>           <td className="px-2 py-1.5 font-mono">groups</td>             <td className="px-2 py-1.5">Team name from Conduit (Executive, Sales, etc.)</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500">If TalentLMS requires the <code className="font-mono">groups</code> claim to be a comma-separated string instead of a JSON array, add a lightweight transformer on their side &mdash; the <code className="font-mono">/userinfo</code> endpoint always returns an array today.</p>
          </Step>

          <Step n={6} title="Turn on OIDC and grab the init URL">
            <p>Save the TalentLMS OIDC form and toggle SSO <b>ON</b>. TalentLMS will show you the <b>OIDC sign-in URL</b>:</p>
            <Copyable label="TalentLMS OIDC init URL" value={talentlmsSsoUrl} testid="tlms-runbook-ssourl" />
            <p className="text-xs text-slate-500">Copy this exact URL &mdash; it&rsquo;s what Conduit&rsquo;s Training app must deep-link to.</p>
          </Step>

          <Step n={7} title="Point Conduit's Training app at the OIDC URL">
            <p>Have your Conduit super-admin run this one-shot switch (or ping the platform team):</p>
            <div className="rounded-md border border-slate-200 bg-slate-900 px-3 py-2 font-mono text-[11px] leading-relaxed text-slate-100">
              curl -X PATCH &quot;{origin}/api/apps/&lt;training-app-id&gt;/sso-provider&quot; \<br />
              &nbsp;&nbsp;-H &quot;Content-Type: application/json&quot; \<br />
              &nbsp;&nbsp;--cookie &quot;session_token=&hellip;&quot; \<br />
              &nbsp;&nbsp;-d &apos;&#123;&quot;sso_provider&quot;: &quot;talentlms_oidc&quot;, &quot;url&quot;: &quot;{talentlmsSsoUrl}&quot;&#125;&apos;
            </div>
            <p className="text-xs text-slate-500">The app now deep-links to TalentLMS OIDC init URL. The iframe navigates there &rarr; TalentLMS 302s to Conduit&rsquo;s <code className="font-mono">/api/oauth/authorize</code> &rarr; Conduit issues a code using the current session cookie &rarr; TalentLMS callback swaps it for tokens and logs the user in.</p>
          </Step>

          <Step n={8} title="Verify end-to-end">
            <ul className="list-disc space-y-1 pl-5 text-xs">
              <li>Log in to Conduit as a <b>super-admin</b>. Click Training. You should land on TalentLMS as the same user &mdash; the old &ldquo;privileges&rdquo; error is gone.</li>
              <li>Log out. Log in as a <b>partner or customer</b> user (e.g. <code className="font-mono">devteam@iplogin.ca</code>). Click Training. You should land in the correct branch as that user.</li>
              <li>Log out and log in as a different user. Click Training. You should land as the <em>new</em> user &mdash; not the previous one.</li>
              <li>In TalentLMS, verify the user&rsquo;s first name / last name / avatar / branch / groups all match Conduit.</li>
            </ul>
          </Step>

          <Step n={9} title="Emergency controls (per-client)">
            <p>If TalentLMS or another OIDC peer is compromised:</p>
            <ul className="list-disc space-y-1 pl-5 text-xs">
              <li><b>Rotate secret</b> &mdash; keeps the client_id, changes the secret. Peer must be reconfigured with the new secret.</li>
              <li><b>Sign out all users</b> &mdash; keeps the secret intact, invalidates every access + refresh token so every user re-authenticates on next request.</li>
              <li><b>Disable</b> &mdash; instantly blocks all new authorize + token requests. Toggle back on when safe.</li>
              <li><b>Delete</b> &mdash; permanent. Removes the client and invalidates every outstanding token.</li>
              <li><b>Rotate signing key</b> (panel header) &mdash; mints a fresh RSA keypair. Old key stays in JWKS for the id_token TTL window so cached verifiers keep working; then retires automatically.</li>
            </ul>
          </Step>
        </div>

        <div className="mt-6 flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button variant="outline" onClick={() => window.open(discovery, "_blank")} data-testid="tlms-runbook-open-discovery">
            <ExternalLink className="h-3.5 w-3.5" /> Open discovery URL
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="tlms-runbook-close">
            Done <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
